import {EBirdMyDataSchema} from "ebird-mydata-reader";
import {DateTime} from "luxon";
import {isUncountableCalibrationObservation} from "./calibratedObservationDisplay";
import {LIST_BUILDING_CHECKLIST_DATE, LIST_BUILDING_CHECKLIST_DATE_LABEL} from "./constants";

export const parseObservationDateTime = (date?: string, time?: string) => {
    if (!date) return undefined;

    const normalizedTime = time?.trim();
    if (normalizedTime) {
        const parsedIsoDateTime = DateTime.fromISO(`${date}T${normalizedTime}`);
        if (parsedIsoDateTime.isValid) return parsedIsoDateTime;

        for (const format of ['h:mm a', 'hh:mm a', 'h:mm:ss a', 'hh:mm:ss a']) {
            const parsedTime = DateTime.fromFormat(normalizedTime.toUpperCase(), format);
            if (parsedTime.isValid) {
                const parsedDate = DateTime.fromISO(date);
                return parsedDate.isValid
                    ? parsedDate.set({
                        hour: parsedTime.hour,
                        minute: parsedTime.minute,
                        second: parsedTime.second,
                        millisecond: 0
                    })
                    : undefined;
            }
        }

        return undefined;
    }

    const parsedDate = DateTime.fromISO(date);
    return parsedDate.isValid ? parsedDate.startOf('day') : undefined;
}

export const generateMarkers = (filteredObservations: EBirdMyDataSchema[]) => {
    const markers: any = {
        type: "FeatureCollection",
        features: []
    };

    let i = 0;
    for (const observation of filteredObservations) {
        const observationDate = parseObservationDateTime(observation.date, observation.time);

        const marker: any = {
            type: "Feature",
            id: i++,
            properties: {
                species: observation.commonName,
                date: observationDate?.toISO(),
                dateMs: observationDate?.toMillis() ?? 0,
                locationId: observation.locationId,
                locationName: observation.location,
                // "image": "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b7/Mourning_Dove_2006.jpg/267px-Mourning_Dove_2006.jpg",
                locationName2: `${observation.county ?? ''} ${observation.stateProvince}`,
                scientificName: observation.scientificName,
                taxonomicOrder: observation.taxonomicOrder,
                mlCatalogNumbers: observation.mlCatalogNumbers,
                count: observation.count,
                isLifer: observation.isLifer ?? false,
                isFirstOfYear: observation.isFirstOfYear ?? false,
                isUncountableCalibrationObservation: isUncountableCalibrationObservation(observation),
                observedYear: observation.observedYear
            },
            geometry: {
                type: "Point",
                coordinates: [
                    observation.longitude,
                    observation.latitude
                ]
            }
        }
        markers.features.push(marker);
    }

    return markers;
}

export const formatDate = (date: string) => {
    if (date.startsWith(LIST_BUILDING_CHECKLIST_DATE)) return LIST_BUILDING_CHECKLIST_DATE_LABEL;

    const dateTime = DateTime.fromISO(date);
    return dateTime.toFormat('cccc, LLLL d, y');
}

export const getFamilySlug = (familyName: string) => {
    return familyName
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
}

export const pluralize = (count: number, singular: string, plural?: string) => {
    if (count === 1) return singular;
    if (plural) return plural;
    if (singular === 'species') return 'species';

    return `${singular}s`;
}
